import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from '../ui/button';

/**
 * Standard fallback UI used by <ErrorBoundary> and by route-level error states.
 *
 * Props:
 *   error    – the thrown Error (optional)
 *   onRetry  – retry handler (optional)
 *   title    – heading override (optional)
 *   message  – body copy override (optional)
 */
export const ErrorFallback = ({ error, onRetry, title, message }) => {
  const heading = title || 'Something went wrong';
  const body =
    message ||
    "We hit an unexpected error while rendering this screen. Try refreshing — if the problem keeps happening, please let us know.";

  return (
    <div
      className="min-h-[60vh] flex items-center justify-center px-6 py-12"
      data-testid="error-fallback"
    >
      <div className="max-w-md text-center space-y-5">
        <div className="mx-auto w-14 h-14 rounded-full bg-red-50 flex items-center justify-center">
          <AlertTriangle className="h-7 w-7 text-primary" />
        </div>

        <div className="space-y-2">
          <h1 className="text-2xl font-bold tracking-tight">{heading}</h1>
          <p className="text-sm text-muted-foreground">{body}</p>
        </div>

        {error?.message && (
          <pre
            className="text-xs text-left bg-secondary/40 border rounded-lg p-3 overflow-auto max-h-40 text-muted-foreground"
            data-testid="error-fallback-detail"
          >
            {error.message}
          </pre>
        )}

        <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
          <Button
            variant="default"
            onClick={() => (onRetry ? onRetry() : window.location.reload())}
            data-testid="error-fallback-retry"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Try again
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              window.location.href = '/';
            }}
            data-testid="error-fallback-home"
          >
            <Home className="h-4 w-4 mr-2" />
            Go home
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ErrorFallback;
